import { SendOptions, Transaction, TransactionSignature, VersionedTransaction } from '@solana/web3.js'

import { getHighPriorityConnection } from '@src/chains/solana/anchor/setup'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('SolanaRetryConnection')

interface SendTransactionOptions {
    skipPreflight?: boolean
    preflightCommitment?: 'processed' | 'confirmed' | 'finalized'
    maxRetries?: number
    forceFallback?: boolean
    useHelius?: boolean
}

/**
 * Default options for Solana transactions
 */
const DEFAULT_SEND_OPTIONS: SendTransactionOptions = {
    skipPreflight: false,
    preflightCommitment: 'processed',
    maxRetries: 5,
    forceFallback: false,
    useHelius: false,
}

/**
 * Sends a transaction using Solana's built-in retry functionality
 *
 * @param transaction The transaction to send (can be legacy or versioned)
 * @param options Send transaction configuration options
 * @returns The transaction signature
 */
export async function sendTransactionWithRetry(
    transaction: Transaction | VersionedTransaction,
    options: SendTransactionOptions = {}
): Promise<TransactionSignature> {
    const opts = { ...DEFAULT_SEND_OPTIONS, ...options }

    // Create a fresh high priority connection for this transaction
    // The Connection class already has retry logic built in
    const connection = getHighPriorityConnection(opts.forceFallback, opts.useHelius)

    // Prepare send options
    const sendOptions: SendOptions = {
        skipPreflight: opts.skipPreflight,
        preflightCommitment: opts.preflightCommitment,
        maxRetries: opts.maxRetries,
    }

    try {
        // Serialize the transaction
        const serializedTx = transaction.serialize()

        // Send the transaction with built-in retries
        const signature = await connection.sendRawTransaction(serializedTx, sendOptions)

        logger.info('Transaction sent successfully', { signature })
        return signature
    } catch (error) {
        // Handle and log the error
        const err = error as any

        if (err?.constructor?.name === 'SendTransactionError') {
            logger.error('Transaction failed with SendTransactionError', {
                errorMessage: err.message,
                errorName: err?.constructor?.name,
                errorCode: err?.code,
                logs: err.logs || [],
            })
        } else {
            logger.error('Transaction failed', {
                errorType: err?.constructor?.name || 'Unknown',
                errorMessage: err.message,
            })
        }

        throw error
    }
}

/**
 * Confirms a transaction using Solana's built-in retry functionality
 *
 * @param signature The transaction signature to confirm
 * @param options Confirmation options
 */
export async function confirmTransactionWithRetry(
    signature: TransactionSignature,
    options: SendTransactionOptions = {}
): Promise<void> {
    const opts = { ...DEFAULT_SEND_OPTIONS, ...options }

    try {
        // Create a fresh connection for confirmation
        const connection = getHighPriorityConnection(opts.forceFallback, opts.useHelius)

        logger.debug('Confirming transaction', {
            signature,
            commitment: opts.preflightCommitment,
        })

        // The confirmTransaction method already has retry logic built in
        await connection.confirmTransaction(signature, opts.preflightCommitment)

        logger.info('Transaction confirmed successfully', { signature })
    } catch (error) {
        const err = error as Error

        logger.error('Transaction confirmation failed', {
            signature,
            errorMessage: err.message,
        })

        throw error
    }
}
